import { Link } from "react-router-dom";
import {
  AttributeItem,
  AttributeList,
} from "@volca/ui/components/ui/attribute-list";
import { Skeleton } from "@volca/ui/components/ui/skeleton";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import type { RecordObject } from "@/profitability/records/objects";
import type { ReportOutput } from "@/profitability/types";
import type { DocumentEntry } from "./document-entries";
import type { DocumentsRun } from "./documents-tab";
import type { DocumentLinks } from "./links";
import { ReadIn } from "./read-in";
import type { DocumentRunRef } from "./types";
import { type DatasetUse, usedFor } from "./used-for";

const DatasetName = ({
  dataset,
  links,
}: {
  dataset: DatasetUse;
  links: DocumentLinks;
}) => {
  const href = dataset.record ? links.record(dataset.id) : null;
  return href ? (
    <Link to={href} className="text-link underline-offset-2 hover:underline">
      {dataset.name}
    </Link>
  ) : (
    dataset.name
  );
};

/**
 * What the file fed in this period's report: the columns, by dataset, whose
 * values the report says come from the input this file filled. Then the
 * periods that read this same file.
 */
export const DocumentUsedFor = ({
  run,
  entry,
  documentId,
  output,
  objects,
  runs,
  runsLoading,
  links,
}: {
  run: DocumentsRun;
  entry: DocumentEntry;
  documentId: string;
  output?: ReportOutput;
  objects: RecordObject[];
  runs: DocumentRunRef[] | undefined;
  runsLoading: boolean;
  links: DocumentLinks;
}) => {
  const use = usedFor(output, objects, entry.slot?.key ?? null);
  return (
    <Stack gap={6} align="stretch" className="max-w-3xl min-w-0">
      <Stack gap={2} align="stretch">
        <Text variant="title" render={<h3 />}>
          In this period&apos;s report
        </Text>
        {!output ? (
          <Skeleton className="h-16 w-full" />
        ) : entry.role === "goal" ? (
          <Text tone="muted">
            Your own numbers are not read into the report: Volca checks the
            report against them once it is built.
          </Text>
        ) : !use.declared ? (
          <>
            <Text tone="muted">
              This report doesn&apos;t say which figures come from which file.
              It produced:
            </Text>
            <Text render={<p />}>
              {use.datasets.map((dataset, index) => (
                <span key={dataset.id}>
                  {index > 0 && ", "}
                  <DatasetName dataset={dataset} links={links} />
                </span>
              ))}
            </Text>
          </>
        ) : use.datasets.length ? (
          <AttributeList className="grid-cols-[minmax(8rem,auto)_minmax(0,1fr)]">
            {use.datasets.map((dataset) => (
              <AttributeItem
                key={dataset.id}
                label={<DatasetName dataset={dataset} links={links} />}
                className="items-start"
              >
                <span className="whitespace-normal">
                  {dataset.columns.join(", ")}
                </span>
              </AttributeItem>
            ))}
          </AttributeList>
        ) : (
          <Text tone="muted">
            No figure in this report says it comes from this file.
          </Text>
        )}
      </Stack>
      <ReadIn
        runs={runs}
        loading={runsLoading}
        currentRunId={run.id}
        documentId={documentId}
        links={links}
      />
    </Stack>
  );
};
